export interface FrameData {
  id: string;
  timestamp: number;
  imageUrl: string; // Base64
  status: 'pending' | 'processing' | 'drafted' | 'healed' | 'verified';
}

export interface DraftResult {
  visualDescription: string;
  transcription: string; // Speech-to-Text
  audioConfidence: number; // Simulated Whisper Log-Probs
  visualConfidence: number;
  linguisticFlags: string[]; // e.g., "Noun Class Mismatch"
  isCodeSwitched?: boolean;
  detectedLanguages?: string[];
}

export interface SyncMapItem extends FrameData {
  draft?: DraftResult;
  language?: string;
  dialect?: string;
  audioKey?: string; // R2 storage key for audio segment
}

export interface IngestionJob {
  id: string;
  filename: string;
  totalFrames: number;
  processedFrames: number;
  status: 'idle' | 'ingesting' | 'splitting' | 'drafting' | 'complete';
  syncMap: SyncMapItem[];
  targetLanguage: string; // e.g., 'zul', 'xho'
  targetDialect: string;
}

export enum ViewState {
  AUTH = 'AUTH',
  INGESTION = 'INGESTION',
  HEALER = 'HEALER',
  VAULT = 'VAULT',
  ADMIN = 'ADMIN'
}

export interface WalletState {
  balance: number; // Current ZAR balance
  tokens: number; // Current 'Verified Tokens'
  payoutThreshold: number; // 1000 tokens
}

export interface TransactionResult {
  success: boolean;
  tokensEarned: number;
  newBalance: number;
  payoutTriggered: boolean;
  payoutReference?: string; // Polar Ref
  message: string;
  impactScoreDelta?: number; // Community ownership metric
}

export interface User {
  uid: string;
  displayName: string;
  email: string;
  role: 'admin' | 'annotator';
  clan: 'Zulu Warriors' | 'Xhosa Guardians' | 'Swahili Sages' | 'Yoruba Kings';
  rank: 'Novice' | 'Healer' | 'Elder' | 'Oracle';
  trustScore: number;
}

export interface VaultItem {
  id: string;
  title: string;
  language: string;
  type: 'Video-Text' | 'Audio-Text' | 'Multimodal';
  size: number; // items
  price: number; // USD
  preview: string;
  afroBenchScore?: number; // 0-100
  biasScore?: 'A' | 'B' | 'C' | 'D'; // Bias Audit Grade
}


// ============================================
// ALKE - African Linguistic Knowledge Engine Types
// ============================================

export type HierarchyLevel = 'phylum' | 'family' | 'subgroup' | 'language' | 'dialect';

export interface LanguageHierarchyNode {
  id: string;
  name: string;
  level: HierarchyLevel;
  isoCode: string | null;
  region: string | null;
  children: LanguageHierarchyNode[];
}

export interface TokenConfidence {
  token: string;
  confidence: number;
  alternatives: Array<{ token: string; confidence: number }>;
  ipaForm: string | null;
  morphemes?: string[];
}

export interface TranscriptionResult {
  rawText: string;
  ipaTranscription: string;
  tokens: TokenConfidence[];
  overallConfidence: number;
  languageDetection: {
    primary: string;
    alternatives: Array<{ code: string; confidence: number }>;
  };
  processingTime: number;
}

export interface MorphemeData {
  id: string;
  position: 'prefix' | 'root' | 'suffix' | 'infix';
  form: string;
  meaning: string | null;
  morphemeType: string;
  order: number;
}

export interface MorphologicalAnalysis {
  wordId: string;
  surfaceForm: string;
  morphemes: MorphemeData[];
  nounClass?: number;
  tense?: string;
  aspect?: string;
  mood?: string;
}

export interface CousinSuggestion {
  sourceWord: string;
  sourceLanguage: string;
  suggestions: Array<{
    word: string;
    language: string;
    dialect: string;
    similarity: number;
    rootMeaning: string;
    ipa: string | null;
  }>;
}

export interface LoraTrainingMetrics {
  languageId: string;
  dialectId: string | null;
  verifiedHours: number;
  totalSegments: number;
  characterErrorRate: number;
  wordErrorRate: number;
  crossLingualScore: number;
  lastUpdated: number;
}

export interface TrainingDashboard {
  summary: {
    totalVerifiedHours: number;
    totalSegments: number;
    languagesWithData: number;
    trainableLanguages: number;
    lexicalGraphNodes: number;
    cognateLinks: number;
  };
  metrics: LoraTrainingMetrics[];
  trainableLanguages: Array<{
    languageId: string;
    languageName: string;
    dialectId?: string;
    dialectName?: string;
    metrics: LoraTrainingMetrics;
    readiness: number;
  }>;
  recentEvaluations: any[];
  graphStats: {
    nodes: number;
    edges: number;
    roots: number;
    avgDegree: number;
  };
}
